import React from 'react';
import { Link } from 'react-router-dom'; // Import Link for routing
import '../App.css';

function Inferences() {
    const sections = [
        {
            title: "Flexibility & Work Arrangements",
            subtitle: "The importance of flexible work schedules.",
            content: "Flexible work schedules allow employees to manage their personal responsibilities alongside professional duties."
        },
        {
            title: "Social Preferences",
            subtitle: "How individuals prefer to interact.",
            content: "Social preferences influence teamwork and overall integration into the company culture."
        },
        {
            title: "Workplace Values & Environment",
            subtitle: "The principles and atmosphere that define a workplace.",
            content: "A positive workplace environment enhances employee satisfaction and productivity."
        },
        {
            title: "Work Style & Independence",
            subtitle: "Explores individual approaches to working.",
            content: "Independence in work styles can lead to increased job satisfaction and performance."
        },
        {
            title: "Career Growth & Expectations",
            subtitle: "Addresses individual aspirations regarding professional development.",
            content: "Career growth opportunities can significantly impact employee motivation and retention."
        }
    ];

    return (
        <div className="inferences-page">
            <h1 className="inferences-title">Risk Assessment</h1>
            <p className="inferences-description">
                This section provides a comprehensive evaluation of potential risks and uncertainties that the company may encounter. It identifies the most relevant risks, including operational, financial, and market-related challenges. We will outline our proactive strategies to mitigate these risks through systematic prototyping and rigorous testing efforts. By employing a structured approach to risk management, we aim to ensure that our future initiatives are resilient and adaptable, ultimately fostering a culture of continuous improvement and innovation.
            </p>
            <h1>Critical Assumption 1</h1>
            <p>We assume the company sample is representative of the culture the company and candidate</p>
            <table className="survey-table">
                <thead>
                    <tr>
                        <th>Hypothesis</th>
                        <th>Test</th>
                        <th>Results</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Company culture survey responses reflect actual workplace environment</td>
                        <td>Compare survey responses with employee interviews and workplace observations</td>
                        <td>80% correlation between survey responses and actual observations</td>
                    </tr>
                </tbody>
            </table>
            <h1 className="inferences-title">Inferences</h1>
            <p className="inferences-description">
                This section addresses key inferences derived from assumption mapping, highlighting the most critical areas for examination and strategic focus.
            </p>
            <div className="inferences-container">
                {sections.map((section, index) => (
                    <div key={index} className="inference-item">
                        <h2 className="inference-title">{section.title}</h2>
                        <h3 className="inference-subtitle">{section.subtitle}</h3>
                        <p className="inference-content">{section.content}</p>
                    </div>
                ))}
                <Link to="/inferences-table" className="read-more-button">More Details</Link>
            </div>

            <p className="inferences-description" style={{marginTop: '8rem'}}>
                This section addresses key inferences derived from assumption mapping, highlighting the most critical areas for examination and strategic focus.
            </p>

            <h2 className="survey-title">Candidate Survey Questions</h2>
            <table className="survey-table">
                <thead>
                    <tr>
                        <th>Candidate Survey Questions</th>
                        <th>Inferences</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Having flexible work hours is really important to me</td>
                        <td>Indicates preference for work-life balance or structured schedules</td>
                    </tr>
                    <tr>
                        <td>You are happy to work overtime to further my career without specific compensation</td>
                        <td>Determination to progress quickly in career and attitude towards high-pressure situations - also indicates work/life balance</td>
                    </tr>
                    <tr>
                        <td>How many days a week would you prefer to be in the office?</td>
                        <td>Gives an impression of how highly the candidate prioritises socialising with colleagues</td>
                    </tr>
                    <tr>
                        <td>It is really important to have flexibility regarding working from home vs working in the office</td>
                        <td>How highly the candidate prioritises socialising with colleagues and balance work/life schedule</td>
                    </tr>
                    <tr>
                        <td>You always like to eat lunch with your colleagues when you are in the office</td>
                        <td>How highly the candidate prioritises socialising with colleagues</td>
                    </tr>
                    <tr>
                        <td>Having a formal dress code is important to you</td>
                        <td>How highly professionalism is valued</td>
                    </tr>
                    <tr>
                        <td>You would always choose to work with a team rather than individually</td>
                        <td>How important control is to the candidate and their attitude to external opinions</td>
                    </tr>
                    <tr>
                        <td>You like to socialise with your colleagues outside of work hours</td>
                        <td>How integrated the candidate wants to be in the company</td>
                    </tr>
                    <tr>
                        <td>How do you prefer to communicate with colleagues?</td>
                        <td>Introvert/extrovert, how focused and direct the candidate is</td>
                    </tr>
                    <tr>
                        <td>What time of day do you feel most productive?</td>
                        <td>How you work</td>
                    </tr>
                    <tr>
                        <td>You feel strongly about the importance of being able to give and receive feedback effectively at work</td>
                        <td>How involved the candidate wants to be in the company</td>
                    </tr>
                    <tr>
                        <td>How often do you expect to have catch-ups with your manager or mentor?</td>
                        <td>How much you need guidance when completing a task - time that the company has to invest in that person</td>
                    </tr>
                    <tr>
                        <td>How do you handle high-pressure situations with direct or intense communication?</td>
                        <td>Introvert/extrovert, how resilient and adaptable the candidate is</td>
                    </tr>
                    <tr>
                        <td>You are happy to take on a project with minimal assistance or guidance</td>
                        <td>How independent the candidate is, how much time will need to be spent on this candidate</td>
                    </tr>
                    <tr>
                        <td>It is important for your workplace to offer flexibility, such as the ability to step out during the day (e.g. for a walk)</td>
                        <td>Attitude towards professionalism and preference regarding structure</td>
                    </tr>
                    <tr>
                        <td>How often are you comfortable with taking on tasks that differ from those outlined directly in your job description?</td>
                        <td>Attitude towards career progression and responsibility</td>
                    </tr>
                    <tr>
                        <td>You prefer a structured mentoring system (e.g., an assigned senior mentor for guidance on career progression and workplace challenges), as opposed to managing your career development independently</td>
                        <td>Attitude towards career progression</td>
                    </tr>
                    <tr>
                        <td>Your manager must demonstrate empathy and support for your well-being as opposed to having a purely professional relationship</td>
                        <td>Level in which candidate values support</td>
                    </tr>
                    <tr>
                        <td>You desire a working environment where you are permitted to listen to music</td>
                        <td>Attitude towards professionalism</td>
                    </tr>
                    <tr>
                        <td>What type of mental health support do you value most in a workplace?</td>
                        <td>Level in which candidate values support</td>
                    </tr>
                </tbody>
            </table>

        </div>
    );
}

export default Inferences;